import { Box, Icon, SxProps, Typography } from "@outschool/backpack";
import { faSearch } from "@outschool/icons";
import { useTranslation } from "@outschool/localization";
import { searchPath } from "@outschool/routes";
import { useTrackEvent } from "@outschool/ui-analytics";
import { TrackedButton } from "@outschool/ui-components-shared";
import {
  Container,
  Form,
  Input,
  Space,
} from "@outschool/ui-legacy-component-library";
import {
  useIsMobile,
  useLinkComponent,
  useNavigation,
} from "@outschool/ui-utils";
import { SystemStyleObject } from "@styled-system/css";
import React, { useState } from "react";

type CatalogSearchProps = {
  heading?: string;
};

const searchFormStyles: SystemStyleObject = {
  backgroundColor: "white",
  border: "1px solid",
  borderColor: "gray4",
  borderRadius: "999px",
  flex: 1,
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
};

const searchInputStyles: SystemStyleObject = {
  flexGrow: 1,
  fontSize: "1.6rem",
  marginLeft: "25px",
  paddingLeft: "0 !important",
  height: "48px !important",
  border: "none !important", // TODO: Better solution for inputs containing buttons
  "&::placeholder": {
    color: "gray4",
  },
  "&:focus": {
    outline: "none",
    border: "none",
  },
};

const searchIconStyles: SxProps = {
  border: 0,
  height: "18px",
  width: "18px",
  marginTop: "0",
  marginRight: "15px",
  fontSize: "2rem",
  color: "#535353",
  background: "transparent",
};

const CategoriesSearchComponent: React.FC<CatalogSearchProps> = props => {
  const { t } = useTranslation(
    "ui-components-website\\CategoriesSearchComponent"
  );
  const isMobile = useIsMobile();
  const heading = props.heading || t("Search classes & tutoring");
  const inputId = "catalog-hub-search";
  const Link = useLinkComponent();

  const popularSearches = [
    "Drawing Anime",
    "Roblox Coding",
    "Chess Camp",
    "World Travel",
    "Food Science",
    "SAT Prep",
    "Piano Lessons",
    "Math Tutoring",
  ];
  const popularSearchesTranslated = [
    t("Drawing Anime"),
    t("Roblox Coding"),
    t("Chess Camp"),
    t("World Travel"),
    t("Food Science"),
    t("SAT Prep"),
    t("Piano Lessons"),
    t("Math Tutoring"),
  ];
  const inputRef = React.createRef<HTMLInputElement>();
  const navigate = useNavigation();
  const [query, setQuery] = useState("");
  const trackEvent = useTrackEvent();

  const handleSubmit = (e: any) => {
    e.preventDefault();
    trackEvent("catalog_hub_search_bar", { query });
    const search = searchPath({ q: query.trim().replace(/\s+/g, " ") });
    navigate(search);
  };

  const handleChange = (e: any) => {
    e.preventDefault();
    setQuery(e.target.value);
  };

  return (
    <Container
      fullbleed={"true"}
      backgroundColor={"peacock50"}
      sx={{ paddingY: isMobile ? "30px" : "45px" }}
    >
      <Typography variant="h3" id={inputId}>
        {heading}
      </Typography>
      <Space y={isMobile ? 20 : 28} />
      <Box flex>
        <Form
          sx={searchFormStyles}
          onSubmit={handleSubmit}
          data-test-id="cs-form"
        >
          <Input
            sx={searchInputStyles}
            type="text"
            aria-labelledby={inputId}
            ref={inputRef}
            placeholder={t("Search for any topic or teacher")}
            onChange={handleChange}
            value={query || ""}
          />
          <Icon sx={searchIconStyles} icon={faSearch} />
        </Form>
      </Box>
      <Space y={isMobile ? 25 : 40} />
      <Typography variant="h5">{t("Popular searches")}</Typography>
      <Space y={isMobile ? 15 : 20} />
      <Box
        flex
        sx={{
          gap: "15px",
          overflow: "auto",
          marginX: isMobile ? "-1em" : "0",
          paddingX: isMobile ? "1em" : "0",
          scrollbarWidth: "none",
          "::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {popularSearches.map((item, index) => {
          return (
            <TrackedButton
              sx={{
                height: "48px",
              }}
              key={index}
              component={Link}
              to={searchPath({ q: item })}
              trackingName="catalog_hub_popular_search_buttons"
              trackingUniqueId={item}
              trackingSharedProperties={{ order: index }}
            >
              {popularSearchesTranslated[index]}
            </TrackedButton>
          );
        })}
      </Box>
    </Container>
  );
};

export default CategoriesSearchComponent;
